import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, RefreshCw } from "lucide-react";

export default function ClientsPage() {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contact_name: "",
    contact_email: "",
    additional_emails: [],
    contact_phone: "",
    address: "",
    daily_rate: "",
    billing_address: "",
    payment_terms: "30"
  });
  const [newEmailInput, setNewEmailInput] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, { headers });
      setClients(response.data);
    } catch (error) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        daily_rate: parseFloat(formData.daily_rate) || 0,
        payment_terms: parseInt(formData.payment_terms) || 30
      };
      
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, data, { headers });
        toast.success("Client updated and synced to all invoices/projects");
      } else {
        await axios.post(`${API}/clients`, data, { headers });
        toast.success("Client created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save client");
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sync/all-clients`, {}, { headers });
      const data = response.data;
      toast.success(`Synced ${data.clients_processed} clients: ${data.invoices_updated} invoices updated`);
    } catch (error) {
      toast.error("Failed to sync client data");
    } finally {
      setSyncing(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      contact_name: client.contact_name || "",
      contact_email: client.contact_email || "",
      additional_emails: client.additional_emails || [],
      contact_phone: client.contact_phone || "",
      address: client.address || "",
      daily_rate: client.daily_rate?.toString() || "",
      billing_address: client.billing_address || "",
      payment_terms: (client.payment_terms ?? 30).toString()
    });
    setNewEmailInput("");
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    
    try {
      await axios.delete(`${API}/clients/${id}`, { headers });
      toast.success("Client deleted successfully");
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: "",
      contact_name: "",
      contact_email: "",
      additional_emails: [],
      contact_phone: "",
      address: "",
      daily_rate: "",
      billing_address: "",
      payment_terms: "30"
    });
    setNewEmailInput("");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <DashboardLayout title="Clients">
      <div className="space-y-6" data-testid="clients-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">Manage your clients and their rates</p>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleSyncAll} 
              disabled={syncing}
              data-testid="sync-all-clients-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync All Data'}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{editingClient ? "Edit Client" : "Add New Client"}</DialogTitle>
                <DialogDescription>
                  {editingClient ? "Update client information" : "Enter the client details below"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Client/Company Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Beaconfield"
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Person</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="e.g., Tom Reilly"
                      data-testid="client-contact-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Contact Email</Label>
                    <Input id="email" type="email" value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="contact@company.com" data-testid="client-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Contact Phone</Label>
                    <Input id="phone" value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+61 400 000 000" data-testid="client-phone-input" />
                  </div>
                </div>
                {/* Additional email recipients */}
                <div className="space-y-2">
                  <Label>Additional Invoice Recipients <span className="text-xs text-muted-foreground">(auto-added as CC when sending invoice)</span></Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={newEmailInput}
                      onChange={e => setNewEmailInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const email = newEmailInput.trim();
                          if (email && !formData.additional_emails.includes(email)) {
                            setFormData({ ...formData, additional_emails: [...formData.additional_emails, email] });
                            setNewEmailInput("");
                          }
                        }
                      }}
                      placeholder="cc@company.com then press Enter"
                      data-testid="additional-email-input"
                    />
                    <button type="button" onClick={() => {
                      const email = newEmailInput.trim();
                      if (email && !formData.additional_emails.includes(email)) {
                        setFormData({ ...formData, additional_emails: [...formData.additional_emails, email] });
                        setNewEmailInput("");
                      }
                    }} className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90">Add</button>
                  </div>
                  {formData.additional_emails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {formData.additional_emails.map((em, i) => (
                        <span key={em} className="flex items-center gap-1 bg-muted px-2 py-1 rounded-full text-xs">
                          {em}
                          <button type="button" onClick={() => setFormData({ ...formData, additional_emails: formData.additional_emails.filter((_, j) => j !== i) })}
                            className="text-muted-foreground hover:text-destructive ml-1">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Client Address (for invoices)</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Tower 4, 727 Collins Street&#10;Melbourne VIC 3000 Australia"
                    rows={2}
                    data-testid="client-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rate">Default Daily Rate (AUD)</Label>
                    <Input
                      id="rate"
                      type="number"
                      step="0.01"
                      value={formData.daily_rate}
                      onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                      placeholder="0.00"
                      data-testid="client-rate-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_address">Your Company Address (for this client&apos;s invoices)</Label>
                  <Textarea
                    id="billing_address"
                    value={formData.billing_address}
                    onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                    placeholder="Leave blank to use default. E.g.:&#10;24 Quarter Circuit&#10;Springvale VIC 3171 Australia"
                    rows={2}
                    data-testid="client-billing-address-input"
                  />
                  <p className="text-xs text-muted-foreground">Different clients can have different sender addresses on invoices</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="payment_terms">Payment Terms (days)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="payment_terms"
                      type="number"
                      min="1"
                      max="365"
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                      placeholder="30"
                      className="w-28"
                      data-testid="client-payment-terms-input"
                    />
                    <span className="text-sm text-muted-foreground">days after invoice date</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Due date will be calculated automatically on each invoice</p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-client-btn">
                    {editingClient ? "Update" : "Create"} Client
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">Loading clients...</div>
              </div>
            ) : clients.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Users className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No clients yet</h3>
                <p className="text-muted-foreground mt-1">Add your first client to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header">Client Name</TableHead>
                    <TableHead className="table-header">Email</TableHead>
                    <TableHead className="table-header">Phone</TableHead>
                    <TableHead className="table-header text-right">Daily Rate</TableHead>
                    <TableHead className="table-header text-center">Payment Terms</TableHead>
                    <TableHead className="table-header text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id} className="hover:bg-accent/50 transition-colors">
                      <TableCell className="font-medium">{client.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.contact_email || "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.contact_phone || "-"}
                      </TableCell>
                      <TableCell className="text-right data-cell">
                        {formatCurrency(client.daily_rate || 0)}
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground text-sm">
                        {client.payment_terms ?? 30} days
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(client)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(client.id)}
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
